import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../utils/api';

const SETUP_ERROR = 'Manual agreement uploads need the Azure database setup. Ask your administrator to apply the host agreement document migration.';
const MAX_BYTES = 10 * 1024 * 1024;

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Date unavailable'
    : date.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

function errorMessage(error, fallback) {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    return data.text().then(text => {
      try { return JSON.parse(text).error || fallback; } catch { return fallback; }
    });
  }
  return Promise.resolve(data?.error || fallback);
}

export default function HostAgreementUploads({ hostId, canManage, compact = false, actionClassName = 'btn btn-secondary btn-sm' }) {
  const [documents, setDocuments] = useState([]);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const inputRef = useRef(null);
  const mountedRef = useRef(true);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/hosts/${hostId}/agreement/uploads`);
      if (!mountedRef.current) return;
      const result = response.data || {};
      setDocuments(Array.isArray(result.documents) ? result.documents : []);
      setAvailable(result.uploadAvailable !== false);
      setNotice('');
      setError('');
      if (result.error && result.uploadAvailable === false) setNotice(result.error);
    } catch (err) {
      const message = await errorMessage(err, 'Could not load uploaded agreements. Try again.');
      if (mountedRef.current) setError(message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [hostId]);

  useEffect(() => {
    mountedRef.current = true;
    loadDocuments();
    return () => { mountedRef.current = false; };
  }, [loadDocuments]);

  const resetPicker = () => {
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleUpload = async event => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError('');
    setNotice('');
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (!isPdf) {
      setError('Choose a PDF file to upload.');
      resetPicker();
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('The PDF must be 10 MB or smaller.');
      resetPicker();
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setBusy(true);
    try {
      const response = await api.post(`/hosts/${hostId}/agreement/uploads`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const document = response.data?.document;
      if (document) setDocuments(current => [document, ...current.filter(item => item.id !== document.id)]);
      else await loadDocuments();
      setError('');
    } catch (err) {
      setError(await errorMessage(err, 'Could not upload this agreement. Try again.'));
    } finally {
      setBusy(false);
      resetPicker();
    }
  };

  const handleDownload = async document => {
    setBusy(true);
    setError('');
    try {
      const response = await api.get(`/hosts/${hostId}/agreement/uploads/${encodeURIComponent(document.id)}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.fileName || 'host-agreement.pdf';
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(await errorMessage(err, `Could not download ${document.fileName || 'this agreement'}.`));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async document => {
    if (!window.confirm(`Remove "${document.fileName}" from uploaded agreements? This cannot be undone.`)) return;
    setBusy(true);
    setError('');
    try {
      await api.delete(`/hosts/${hostId}/agreement/uploads/${encodeURIComponent(document.id)}`);
      setDocuments(current => current.filter(item => item.id !== document.id));
    } catch (err) {
      setError(await errorMessage(err, `Could not remove ${document.fileName}.`));
    } finally {
      setBusy(false);
    }
  };

  const uploadDisabled = !available || busy || loading;
  const buttonStyle = { opacity: busy ? 0.6 : 1, cursor: busy ? 'wait' : 'pointer' };
  const subdued = { fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.45 };

  return (
    <section style={{ padding: compact ? '0' : '16px 18px', border: compact ? 'none' : '1px solid var(--line)', borderRadius: compact ? 0 : 10, background: compact ? 'transparent' : 'var(--bg-elev)', marginTop: compact ? 0 : 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <h4 style={{ margin: 0, color: 'var(--ink-3)', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>Uploaded agreements</h4>
        {canManage && (
          <>
            <input ref={inputRef} type="file" accept="application/pdf,.pdf" onChange={handleUpload} style={{ display: 'none' }} aria-label="Choose an agreement PDF" />
            <button
              type="button"
              className={actionClassName}
              style={{ opacity: uploadDisabled ? 0.6 : 1, cursor: uploadDisabled ? 'not-allowed' : 'pointer' }}
              disabled={uploadDisabled}
              onClick={() => inputRef.current?.click()}
            >
              {busy ? 'Working…' : '+ Upload agreement'}
            </button>
          </>
        )}
      </div>
      {!available && (
        <div role="status" style={{ padding: '9px 11px', borderRadius: 7, border: '1px solid #ead6a8', background: '#fff8e8', color: '#765516', fontSize: 12, lineHeight: 1.45, marginBottom: 10 }}>
          {notice || SETUP_ERROR}
          {canManage && <div style={{ marginTop: 4, fontWeight: 600 }}>Upload is unavailable until the database setup is complete.</div>}
        </div>
      )}
      {error && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, color: 'var(--status-danger-fg, #B3261E)', fontSize: 12, margin: '8px 0' }}>
          <span>{error}</span>
          {loading && <span>Retrying…</span>}
        </div>
      )}
      {loading ? (
        <div aria-label="Loading uploaded agreements" style={{ display: 'grid', gap: 7, paddingTop: 3 }}>
          {[0, 1].map(i => <div key={i} style={{ height: 40, borderRadius: 6, background: 'var(--line-2, #edf0f2)', opacity: i ? 0.55 : 0.8 }} />)}
        </div>
      ) : documents.length ? (
        <div style={{ display: 'grid', gap: 7 }}>
          {documents.map(document => (
            <div key={document.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '9px 10px', border: '1px solid var(--line)', borderRadius: 7, background: 'var(--bg)' }}>
              <div style={{ minWidth: 0, flex: '1 1 160px' }}>
                <div title={document.fileName} style={{ color: 'var(--ink)', fontSize: 12.5, fontWeight: 600, overflowWrap: 'anywhere' }}>{document.fileName}</div>
                <div style={{ ...subdued, marginTop: 2 }}>
                  {[formatDate(document.createdAt), formatBytes(document.sizeBytes), document.uploadedByName].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button type="button" className={actionClassName} style={buttonStyle} disabled={busy} onClick={() => handleDownload(document)}>Download</button>
                {canManage && <button type="button" className={`${actionClassName} btn-danger-ghost`} style={buttonStyle} disabled={busy} onClick={() => handleRemove(document)}>Remove</button>}
              </div>
            </div>
          ))}
        </div>
      ) : !error ? (
        <div style={{ padding: '12px 10px', border: '1px dashed var(--line)', borderRadius: 7, color: 'var(--ink-3)', fontSize: 12 }}>
          No manually uploaded agreement files yet.
        </div>
      ) : (
        <button type="button" className={actionClassName} style={{ ...buttonStyle, marginTop: 3 }} onClick={loadDocuments} disabled={loading}>Retry loading</button>
      )}
    </section>
  );
}
